import * as React from 'react';
import { useEffect } from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';

import { AiQuotaPaywallEventAdapter } from '~analytics/adapters/AiQuotaPaywallEventAdapter';
import type { AiFeatureType } from '~analytics/adapters/AiQuotaPaywallEventAdapter';
import { AiQuotaUpgradedEventAdapter } from '~analytics/adapters/AiQuotaUpgradedEventAdapter';
import { translate } from '~l10n/client';
import { IconButton } from '~shared/components/buttons';
import { IconX } from '~shared/components/icons/standard';
import { Link } from '~shared/components/Link';
import { useCurrentPlan } from '~teacher/components/PricingTable/hooks';

import { InlineMessage } from '../InlineMessage/InlineMessage';
import { QuotaBucket } from './types';

const eightyPercentQuotaMessages: Record<
  QuotaBucket,
  {
    message: string;
  }
> = {
  Import: {
    message: translate(
      'Import limit almost reached. Upgrade to keep importing this week.'
    ),
  },
  Generate: {
    message: translate(
      'Question generation limit almost reached. Upgrade to keep creating this week.'
    ),
  },
};

interface EightyPercentQuotaInlineMessageProps {
  bucket: QuotaBucket;
  onClose: () => void;
  upgradeHref: string;
  percentUsed?: number;
}

const BUCKET_TO_FEATURE_MAP: Record<QuotaBucket, AiFeatureType> = {
  Generate: 'generate',
  Import: 'copypaste',
};

export function EightyPercentQuotaInlineMessage({
  upgradeHref,
  bucket,
  onClose,
  percentUsed = 80,
}: EightyPercentQuotaInlineMessageProps) {
  const currentPlanQuery = useCurrentPlan();
  const currentPlan = currentPlanQuery.data ?? PlanType.Free;
  const featureType = BUCKET_TO_FEATURE_MAP[bucket];

  useEffect(() => {
    new AiQuotaPaywallEventAdapter({
      featureType,
      limitType: 'soft',
    }).sendAnalytics();
  }, [featureType]);

  const handleUpgradeClick = () => {
    new AiQuotaUpgradedEventAdapter({
      featureType,
      limitType: 'soft',
      percentUsed,
      currentPlan,
    }).sendAnalytics();
  };

  return (
    <InlineMessage appearance="neutral">
      <div className="flex gap-2">
        <div className="flex-grow-0 flex-shrink">
          {eightyPercentQuotaMessages[bucket].message}{' '}
          <Link
            className="group relative rounded-1 outline-none transition ease-in-out duration-150 text-grey-900 hocus:text-grey-700 font-700"
            href={upgradeHref}
            onClick={handleUpgradeClick}
          >
            {translate('Upgrade')}
          </Link>
        </div>
        <div className="flex-grow-0 flex-shrink-0">
          <IconButton appearance="ghost" onClick={onClose}>
            <IconX title="Close" titleId="close-inline-message" />
          </IconButton>
        </div>
      </div>
    </InlineMessage>
  );
}
